import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, AlertCircle, Layers, Plug, Users, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { dollars } from "@/lib/money";
import { Progress } from "./ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";
import { useRealtime } from "@/lib/realtime";
import { Badge } from "./ui/badge";

/**
 * Top-of-screen ops snapshot. Polled every 10s; realtime events bump
 * the queue counter early so it doesn't lag visibly.
 */
export function StatusStrip() {
  const { data, isError } = useQuery({
    queryKey: ["system", "health"],
    queryFn: () => api.systemHealth(),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // Realtime nudge — when a new queue job lands, refetch sooner.
  useRealtime("queue_jobs", () => {
    /* RQ refetch handled by interval; flicker would be worse than 10s lag */
  });

  const queueDepth = data?.queue_depth ?? 0;
  const queueTone =
    queueDepth > 200 ? "text-destructive" : queueDepth > 80 ? "text-warning" : "text-foreground";

  const costToday = data?.cost_today_cents ?? 0;
  const costCeiling = data?.cost_ceiling_cents ?? 4000;
  const costPct = costCeiling > 0 ? Math.min(100, (costToday / costCeiling) * 100) : 0;
  const costTone = costPct >= 100 ? "danger" : costPct >= 75 ? "warning" : "brand";

  const bridges = data?.bridges ?? [];
  const allHealthy = bridges.length > 0 && bridges.every((b) => b.status === "healthy");
  const anySilent = bridges.some((b) => b.status === "silent");

  return (
    <div className="flex h-full items-center gap-5 px-5 text-xs">
      <Tile
        icon={<Layers className="h-3.5 w-3.5" />}
        label="Queue"
        valueClass={cn("tnum", queueTone)}
        value={queueDepth.toLocaleString()}
      />
      <div className="flex min-w-[220px] items-center gap-2">
        <Zap className="h-3.5 w-3.5 text-muted-foreground" />
        <div className="flex-1">
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground">Today</span>
            <span className="tnum text-foreground">
              {dollars(costToday)} <span className="text-muted-foreground">/ {dollars(costCeiling)}</span>
            </span>
          </div>
          <Progress value={costPct} tone={costTone} className="mt-1 h-1" />
        </div>
      </div>

      <Tile
        icon={<Users className="h-3.5 w-3.5" />}
        label="Active"
        value={(data?.active_creators ?? 0).toLocaleString()}
      />

      <Link to="/" className="rounded">
        <Tile
          icon={<AlertCircle className="h-3.5 w-3.5" />}
          label="Drafts"
          value={String(data?.pending_drafts ?? 0)}
          highlight={Boolean((data?.pending_drafts ?? 0) > 0)}
        />
      </Link>

      <div className="ml-auto flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Badge
                tone={allHealthy ? "success" : anySilent ? "danger" : "muted"}
                className="gap-1.5"
              >
                <Plug className="h-3 w-3" />
                {bridges.length === 0
                  ? "no bridges"
                  : allHealthy
                    ? `${bridges.length} bridge${bridges.length > 1 ? "s" : ""}`
                    : `${bridges.filter((b) => b.status === "silent").length} silent`}
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {bridges.length === 0 ? (
              <span>No bridges connected</span>
            ) : (
              <ul className="space-y-0.5">
                {bridges.map((b) => (
                  <li key={b.host_id} className="flex gap-2">
                    <span className={b.status === "healthy" ? "text-success" : "text-destructive"}>
                      ●
                    </span>
                    <span className="font-mono">{b.host_id}</span>
                  </li>
                ))}
              </ul>
            )}
          </TooltipContent>
        </Tooltip>

        <Badge tone={isError ? "danger" : "muted"} className="gap-1">
          <Activity className="h-3 w-3" /> {isError ? "api offline" : "live"}
        </Badge>
      </div>
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  valueClass,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClass?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors",
        highlight && "bg-brand/10 text-brand",
      )}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("tnum font-semibold", valueClass)}>{value}</span>
    </div>
  );
}
